import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { coverageScreenQuery, downloadCoveragePdf, downloadCoverageXlsx } from '../../api/admCoverage'
import { shellQuery } from '../../api/shell'
import { Content } from '../../ui/Shell'
import { toast } from '../../ui/notify'
import { usePageStyles } from '../../ui/usePageStyles'
import { AdmPageHead } from './AdmPageHead'
import { AdmFilterBar, WindowPicker } from './AdmFilterBar'
import { AdmViews } from './AdmViews'

/**
 * ADM -> Coverage, din `views/version-3/softsee/adm/coverage/index.php`.
 *
 * DOVADA DIN SPATELE CERINTEI 73. Contractul pune doua tinte, iar una care se
 * poate dovedi doar re-rulind o interogare inceata in ultima zi a proiectului
 * nu e o dovada. Slujba de rezumat scrie cifrele zi de zi; ecranul le citeste.
 *
 * TREI NUMERE, ANUME NEADUNATE INTR-UNUL: configurat, gasit, corelat. Un singur
 * procent ascunde care din trei e cel scurt, si au trei leacuri diferite - mai
 * multe intervale, mai multe acreditari, mai multa telemetrie.
 *
 * TINTELE SE SCRIU (28 octombrie 2026): 80% descoperit, 70% corelat, in
 * banda de sus si in capul coloanelor. O bara sub tinta e galbena - altfel
 * ecranul raspundea "cat" si tacea la "e destul?", adica tocmai la ce cere
 * cerinta 73.
 *
 * DEASUPRA TABELULUI statea tendinta desenata; o desena amCharts, care cere
 * licenta comerciala, si a iesit din portal pe 6 septembrie 2026. Nu s-a mutat
 * pe alta unealta - s-a sters. Tabelul pe clienti, cel care se si exporta,
 * ramane.
 *
 * CU BANDA DE VEDERI. Pana pe 28 octombrie 2026 ecranul n-avea nicio intrare
 * - nici in bara Softsee, nici in banda - si nu-l gasea nimeni. Acum e o fila
 * a descoperirii, are rand in bara modulului ("ADM Coverage"), si cartonasul
 * de pe tablou duce aici.
 */

const STYLES = ['css/adm.css']

function count(value: number): string {
  return value.toLocaleString('en-US')
}

export function CoveragePage({ days }: { days: number }) {
  const shellData = useQuery(shellQuery('softsee'))
  usePageStyles(STYLES, shellData.data?.version ?? '')

  const [busy, setBusy] = useState<'' | 'xlsx' | 'pdf'>('')
  const screen = useQuery(coverageScreenQuery(days))

  const rows = screen.data?.rows ?? []
  const targets = screen.data?.targets ?? { coverage: 80, correlation: 70 }
  const missing = screen.data?.missing ?? []

  const save = async (what: 'xlsx' | 'pdf') => {
    setBusy(what)
    try {
      await (what === 'xlsx' ? downloadCoverageXlsx(days) : downloadCoveragePdf(days))
    } catch (error) {
      toast((error as Error).message, 'Coverage', 'error')
    } finally {
      setBusy('')
    }
  }

  return (
    <Content>
      <div id="content">
        <AdmPageHead
          tone="blue-mauve"
          icon="fa-bullseye"
          title="Coverage"
          note="configured against found against correlated, measured per customer. Requirement 73"
          stats={[
            { value: count(rows.length), label: 'customers', tone: 'blue-mauve' },
            { value: count(screen.data?.itemsOnRecord ?? 0), label: 'items on record', tone: 'grey' },
            { value: `${targets.coverage}%`, label: 'discovery target', tone: 'grey' },
            { value: `${targets.correlation}%`, label: 'correlation target', tone: 'grey' },
          ]}
        />

        <AdmViews views={screen.data?.views ?? []} current="coverage" />

        <AdmFilterBar>
          <WindowPicker
            windows={screen.data?.windows ?? [1, 7, 30, 90]}
            value={days}
            onChange={(next) => window.location.assign(`/app/adm/coverage?days=${next}`)}
          />

          <button
            type="button"
            className="btn btn-default btn-sm"
            style={{ marginBottom: 2 }}
            id="admCoverageXlsx"
            disabled={busy !== ''}
            onClick={() => void save('xlsx')}
          >
            <i className="fa fa-file-excel"></i> {busy === 'xlsx' ? 'Preparing…' : 'Excel'}
          </button>

          {/* Cu o foaie lucreaza cineva; un PDF se semneaza si se pune la dosar,
              iar pentru o tinta contractata aia e chiar rostul lui. Butonul apare
              numai daca serverul chiar poate scoate unul - vezi `pdfAvailable`. */}
          {screen.data?.pdfAvailable ? (
            <button
              type="button"
              className="btn btn-default btn-sm"
              style={{ marginBottom: 2 }}
              id="admCoveragePdf"
              disabled={busy !== ''}
              onClick={() => void save('pdf')}
            >
              <i className="fa fa-file-pdf"></i> {busy === 'pdf' ? 'Preparing…' : 'PDF'}
            </button>
          ) : null}
        </AdmFilterBar>

        <div className="adm-section">
          <h4>Per customer</h4>
          <span className="adm-section-note">{screen.data?.day ? `as of ${screen.data.day}` : ''}</span>
        </div>

        {screen.isPending ? (
          <div className="adm-empty-inline">
            <i className="fa fa-spinner fa-spin"></i> Looking…
          </div>
        ) : screen.isError ? (
          <div className="adm-empty-inline">{(screen.error as Error).message}</div>
        ) : rows.length === 0 ? (
          <div className="adm-empty-inline">
            <i className="fas fa-bullseye"></i> The daily summary has not run yet, so there is nothing to measure against.
          </div>
        ) : (
          <table className="table table-hover adm-list" id="admCoverageTable" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Customer</th>
                <th className="adm-numeric">Configuration items</th>
                <th className="adm-numeric">With agent</th>
                <th className="adm-numeric">Collecting</th>
                <th className="adm-numeric">Correlated</th>
                <th style={{ width: 160 }}>Discovered (target {targets.coverage}%)</th>
                <th style={{ width: 160 }}>Correlated (target {targets.correlation}%)</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.company_id}>
                  <td>{row.company_name}</td>
                  <td className="adm-numeric">{count(row.ci_total)}</td>
                  <td className="adm-numeric">{count(row.ci_with_agent)}</td>
                  <td className="adm-numeric">{count(row.ci_with_telemetry)}</td>
                  <td className="adm-numeric">{count(row.flows_correlated)}</td>
                  <td>
                    <div className={row.coverage_met ? 'adm-progress' : 'adm-progress adm-progress-warn'} title={`target ${targets.coverage}%`}>
                      <span style={{ width: `${Math.min(100, row.coverage_pct)}%` }}></span>
                      <em>{row.coverage_pct}%</em>
                    </div>
                  </td>
                  <td>
                    <div className={row.correlation_met ? 'adm-progress' : 'adm-progress adm-progress-warn'} title={`target ${targets.correlation}%`}>
                      <span style={{ width: `${Math.min(100, row.correlation_pct)}%` }}></span>
                      <em>{row.correlation_pct}%</em>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {missing.length > 0 ? (
          /* Golul pe care un procent nu-l poate arata: un client fara cifra nu e
             la 0% acoperire, e un client impotriva caruia descoperirea n-a rulat
             niciodata. Sunt doua discutii diferite. */
          <>
            <div className="adm-section">
              <h4>Never measured</h4>
              <span className="adm-section-note">no discovery summary has ever been written for these customers</span>
            </div>

            <table className="table adm-list" id="admCoverageMissing">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th className="adm-numeric">Devices on record</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {missing.map((company) => (
                  <tr key={company.ID}>
                    <td>{company.company_name}</td>
                    <td className="adm-numeric">{count(company.devices)}</td>
                    <td className="text-right">
                      {/* Programarea inca sta in portal; legatura ramane cea veche
                          pana se muta ecranul ei. */}
                      <a className="btn btn-default btn-xs" href={`/app/adm/schedules?company_id=${company.ID}`}>
                        Set up discovery
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}

        <p className="text-muted small" style={{ marginTop: 12 }}>
          <a href="/app/adm/dashboard">Back to discovery</a>
        </p>
      </div>
    </Content>
  )
}
